import { type FC } from "react";

import { INDEX_NOT_FOUND } from "components/constants";
import {
  INTERACTION_MODE_ICONS,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "components/materials/page/interaction-mode/helpers";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { DualButton } from "mds/components/DualButton";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectHasSubmissionsForPageId } from "store/selectors";

interface GroupButtonProps {
  title: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  page: PageType;
  text: string;
}

const GroupButton: FC<GroupButtonProps> = ({ title, onClick, page, text }) => {
  return (
    <Chip
      as="button"
      disabled={!onClick}
      eventName="Group Settings - Toggle"
      kind="hibiscus"
      title={title}
      onClick={onClick}
    >
      <IconText
        containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
        containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
        iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
        text={text}
      />
    </Chip>
  );
};

interface GroupMenuButtonProps {
  page: PageType;
  onGroupConfigCardToggle?: (interactionMode: InteractionMode) => void;
  onPageGroupInfoCardToggle?: () => void;
  pageGroupIndex: number;
  /** When the GroupMenuButton is restricted, this means that instructors:
   *   - will not see the dual group menu button (they may not modify the page's interaction mode).
   *   - will be able to click on the group button to see either 1) the page group info card or 2) the group config card.
   *   - will only be allowed the following actions on the group config card: 1) drag and drop individual students
   *   or 2) create another page group. */
  restricted: boolean;
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const GroupMenuButton = ({
  ref,
  page,
  onGroupConfigCardToggle,
  onPageGroupInfoCardToggle,
  pageGroupIndex,
  restricted,
  onClick,
  onKeyDown,
}: GroupMenuButtonProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const hasSubmissions = useAppSelector((s) => selectHasSubmissionsForPageId(s, page.id));
  const shouldOpenPageGroupInfoCard =
    pageGroupIndex !== INDEX_NOT_FOUND && onPageGroupInfoCardToggle;

  if (page.interaction_mode === InteractionMode.STATIC) {
    return null;
  }

  const buttonTitle = shouldOpenPageGroupInfoCard
    ? t("common.view_group", {
        number: pageGroupIndex + 1,
      })
    : t("tooltip.configure_groups");

  const buttonText = shouldOpenPageGroupInfoCard
    ? t("common.group_title", {
        number: pageGroupIndex + 1,
      })
    : t(`page_interaction_mode_menu.interaction_mode.${page.interaction_mode}`);

  const onGroupButtonClick =
    !shouldOpenPageGroupInfoCard && !onGroupConfigCardToggle
      ? undefined
      : (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.stopPropagation();
          e.preventDefault();
          if (shouldOpenPageGroupInfoCard) {
            onPageGroupInfoCardToggle();
          } else {
            onGroupConfigCardToggle?.(page.interaction_mode);
          }
        };

  const groupButton = (
    <GroupButton
      page={page}
      text={buttonText}
      title={!restricted ? buttonTitle : ""}
      onClick={onGroupButtonClick}
    />
  );

  if (restricted) {
    if (hasSubmissions || page.submissions_last_collected_at) {
      return <Tooltip content={t("tooltip.group_config_has_submissions")}>{groupButton}</Tooltip>;
    }

    return groupButton;
  }

  return (
    // If the page has a group interaction mode, then we want to show a DualButton, the left half
    // of which just re-opens the menu without displaying the dropdown, and the right half of which
    // displays a dropdown which then allows changing of interaction mode.
    <DualButton
      buttonLeft={groupButton}
      buttonRight={
        <Chip
          as="button"
          className="!px-2"
          kind="hibiscus"
          title={t("tooltip.change_type_of_activity")}
        >
          <ChevronDownIcon />
        </Chip>
      }
      ref={ref}
      onClick={onClick}
      onKeyDown={onKeyDown}
    />
  );
};

GroupMenuButton.displayName = "GroupMenuButton";
